import React from 'react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { CheckCircle, Info, Search, Loader2, Download, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from './constants';
import { getToken } from '../../services/tokenService';

export const ContratParentSearch = ({ ctx }) => {
  const {
    modeSimulateur, referenceRecherche, setReferenceRecherche, erreurContrat, setErreurContrat,
    handleRechercherContrat, loadingContrat, contratsDisponibles, handleSelectContrat,
    contratParent, loadingContrats, handleResetAvenant,
  } = ctx;
  if (modeSimulateur !== 'avenant') return null;
  return (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-5 rounded-xl border-2 border-purple-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-purple-600 rounded-lg">
                      <Search className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-purple-800">Recherche du contrat parent</h4>
                      <p className="text-xs text-purple-600">Sélectionnez le contrat sur lequel créer l'avenant</p>
                    </div>
                  </div>
                  
                  {/* Recherche par référence */}
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <Label htmlFor="reference" className="text-sm font-semibold text-purple-700 mb-2 block">
                          Recherche par référence (N° Police)
                        </Label>
                        <Input
                          id="reference"
                          type="text"
                          placeholder="Ex: POL20250115ABCD1234 ou REF-SOLIDAIRE-001"
                          value={referenceRecherche}
                          onChange={(e) => {
                            setReferenceRecherche(e.target.value);
                            if (erreurContrat) setErreurContrat('');
                          }}
                          className="border-purple-300 focus:border-purple-500 focus:ring-purple-500"
                          onKeyDown={(e) => e.key === 'Enter' && handleRechercherContrat()}
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          onClick={handleRechercherContrat}
                          disabled={loadingContrat}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          {loadingContrat ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Search className="h-4 w-4" />
                          )}
                          <span className="ml-2">Rechercher</span>
                        </Button>
                      </div>
                    </div>
                    
                    {/* Ou sélection depuis liste */}
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-purple-200" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-purple-50 px-3 text-purple-500 font-semibold">ou sélectionnez</span>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-semibold text-purple-700 mb-2 block">
                        Contrats disponibles ({contratsDisponibles.length})
                      </Label>
                      <select
                        className="w-full p-3 border-2 border-purple-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 bg-white text-slate-700"
                        onChange={(e) => handleSelectContrat(e.target.value)}
                        value={contratParent?.id || contratParent?.numero_police || ''}
                        disabled={loadingContrats}
                      >
                        <option value="">
                          {loadingContrats ? 'Chargement...' : '-- Sélectionnez un contrat --'}
                        </option>
                        {contratsDisponibles.map((contrat) => {
                          const ref = contrat.numero_police || contrat.ref_solidaire || contrat.id;
                          const nom = contrat.raison_sociale || contrat.nom_gerant || 'N/A';
                          const dateFin = contrat.date_fin || contrat.periode_adhesion_fin;
                          const dateFinFormatted = dateFin ? new Date(dateFin).toLocaleDateString('fr-FR') : 'N/A';
                          return (
                            <option key={contrat.id || ref} value={contrat.id || ref}>
                              {ref} - {nom} (expire le {dateFinFormatted})
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    
                    {/* Erreur de recherche */}
                    {erreurContrat && (
                      <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-red-700">Contrat non éligible</p>
                            <p className="text-sm text-red-600 mt-1">{erreurContrat}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Affichage du contrat sélectionné */}
                    {contratParent && (
                      <div className="p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-green-600 rounded-lg">
                            <CheckCircle className="h-5 w-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h5 className="font-bold text-green-800 text-sm">Contrat sélectionné</h5>
                              <Badge className="bg-green-600 text-white text-xs font-semibold">Valide</Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-green-600">Référence : </span>
                                <span className="font-semibold text-green-800">{contratParent.numero_police || contratParent.ref_solidaire}</span>
                              </div>
                              <div>
                                <span className="text-green-600">Entreprise : </span>
                                <span className="font-semibold text-green-800">{contratParent.raison_sociale || `${contratParent.prenom_gerant || ''} ${contratParent.nom_gerant || ''}`.trim() || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="text-green-600">Date de fin : </span>
                                <span className="font-semibold text-green-800">
                                  {contratParent.date_fin || contratParent.periode_adhesion_fin 
                                    ? new Date(contratParent.date_fin || contratParent.periode_adhesion_fin).toLocaleDateString('fr-FR')
                                    : 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className="text-green-600">Montant actuel : </span>
                                <span className="font-semibold text-green-800">{(contratParent.montant_total || contratParent.montant_ht_contribution || 0).toLocaleString('fr-FR')} €</span>
                              </div>
                            </div>
                            
                            {/* Bouton attestation signée */}
                            <div className="mt-3 pt-2 border-t border-green-200 flex items-center justify-between">
                              <p className="text-xs text-green-700 italic">
                                L'avenant héritera de la date de fin : <strong>{contratParent.date_fin || contratParent.periode_adhesion_fin 
                                  ? new Date(contratParent.date_fin || contratParent.periode_adhesion_fin).toLocaleDateString('fr-FR')
                                  : 'N/A'}</strong>
                              </p>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    const token = getToken();
                                    const response = await axios.get(
                                      `${API}/souscriptions-rcd/${contratParent.id}/documents/rcds_unique`,
                                      { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
                                    );
                                    const url = window.URL.createObjectURL(new Blob([response.data]));
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.setAttribute('download', `Attestation_${contratParent.numero_police || contratParent.id}.pdf`);
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                    window.URL.revokeObjectURL(url);
                                    toast.success('Attestation téléchargée !');
                                  } catch (error) {
                                    toast.error('Impossible de télécharger l\'attestation');
                                  }
                                }}
                                className="text-xs h-7 border-green-400 text-green-700 hover:bg-green-100"
                              >
                                <Download className="h-3 w-3 mr-1" />
                                Attestation signée
                              </Button>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleResetAvenant}
                            className="text-green-600 hover:text-green-800 hover:bg-green-100 h-7 text-xs"
                          >
                            Changer
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Bloquer la suite si pas de contrat sélectionné */}
                  {!contratParent && (
                    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-2 text-amber-700">
                        <Info className="h-4 w-4" />
                        <p className="text-sm font-medium">Sélectionnez un contrat parent pour continuer</p>
                      </div>
                    </div>
                  )}
                </div>
  );
};
